import Joi from 'joi';

import { AppSubjectEnum } from '../../../../shared/modules/authz/enums/app-subject.enum';
import { createPoliciesAssignSchema } from '../../../policies/validators/schemas/policies-assign.schema';
import { FuelConsumptionUnitEnum } from '../../../shared/enums/fuel-consumption-unit.enum';
import { createTagsSchema } from '../../../tags/validators/schemas/create-tags.schema';
import { VehicleAllocationEnum } from '../../enums/vehicle-allocation.enum';
import { VehicleBodyTypeEnum } from '../../enums/vehicle-body-type.enum';
import { VehicleDriveTrainTypeEnum } from '../../enums/vehicle-drive-train-type.enum';
import { VehicleElectricBatteryTypeEnum } from '../../enums/vehicle-electric-battery-type.enum';
import { VehicleEmissionsUnitCO2Enum } from '../../enums/vehicle-emissions-unit-co2.enum';
import { VehicleEngineSizeUnitEnum } from '../../enums/vehicle-engine-size-unit.enum';
import { VehicleFuelTankCapacityUnitEnum } from '../../enums/vehicle-fuel-tank-capacity-unit.enum';
import { VehicleFuelTypeEnum } from '../../enums/vehicle-fuel-type.enum';
import { VehicleOdometerUnitEnum } from '../../enums/vehicle-odometer-unit.enum';
import { VehiclePowerPlantTypeEnum } from '../../enums/vehicle-power-plant-type.enum';
import { VehicleSteeringPositionEnum } from '../../enums/vehicle-steering-position.enum';
import { VehicleTransmissionTypeEnum } from '../../enums/vehicle-transmission-type.enum';
import { VehicleTypeEnum } from '../../enums/vehicle-type.enum';

/**
 * Schema for new vehicle record validation.
 */
export const createVehicleSchema = () =>
  Joi.object({
    color: Joi.string().optional().allow(null, ''),

    vin: Joi.alternatives()
      .conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid': 'validations|vehicle-vin|cannot-be-null',
        }),
        otherwise: Joi.string().max(34).required().messages({
          'any.required': 'validations|vehicle-vin|required',
          'string.base': 'validations|vehicle-vin|must-be-string',
          'string.empty': 'validations|vehicle-vin|cannot-be-empty',
          'string.max': 'validations|vehicle-vin|too-long',
        }),
      })
      .required()
      .messages({
        'any.required': 'validations|vehicle-vin|required',
      }),

    type: Joi.alternatives()
      .conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid': 'validations|vehicle-type|cannot-be-null',
        }),
        otherwise: Joi.string()
          .valid(...Object.values(VehicleTypeEnum))
          .required()
          .messages({
            'any.required': 'validations|vehicle-type|required',
            'any.only': 'validations|vehicle-type|invalid-value',
            'string.base': 'validations|vehicle-type|must-be-string',
            'string.empty': 'validations|vehicle-type|cannot-be-empty',
          }),
      })
      .required()
      .messages({
        'any.required': 'validations|vehicle-type|required',
      }),

    allocation: Joi.alternatives()
      .conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid': 'validations|vehicle-allocation|cannot-be-null',
        }),
        otherwise: Joi.string()
          .valid(...Object.values(VehicleAllocationEnum))
          .required()
          .messages({
            'any.required': 'validations|vehicle-allocation|required',
            'any.only': 'validations|vehicle-allocation|invalid-value',
            'string.base': 'validations|vehicle-allocation|must-be-string',
            'string.empty': 'validations|vehicle-allocation|cannot-be-empty',
          }),
      })
      .required()
      .messages({
        'any.required': 'validations|vehicle-allocation|required',
      }),

    fuelType: Joi.string()
      .valid(...Object.values(VehicleFuelTypeEnum))
      .allow(null)
      .messages({
        'string.base': 'validations|vehicle-fuel-type|must-be-string',
        'any.only': 'validations|vehicle-fuel-type|invalid-value',
      }),

    licensePlate: Joi.string().allow(null, '').messages({
      'string.base': 'validations|vehicle-license-plate|must-be-string',
    }),

    engineNumber: Joi.string().allow(null, '').messages({
      'string.base': 'validations|vehicle-engine-number|must-be-string',
    }),

    registeredAt: Joi.date().allow(null).messages({
      'date.base': 'validations|vehicle-registered-at|must-be-a-valid-date',
    }),

    deregisteredAt: Joi.date().allow(null).messages({
      'date.base': 'validations|vehicle-deregistered-at|must-be-a-valid-date',
    }),

    makeId: Joi.number().allow(null).messages({
      'number.base': 'validations|vehicle-make-id|must-be-number',
    }),

    modelId: Joi.number().allow(null).messages({
      'number.base': 'validations|vehicle-model-id|must-be-number',
    }),

    year: Joi.number().min(2000).allow(null).messages({
      'number.base': 'validations|vehicle-year|must-be-number',
      'number.min': 'validations|vehicle-year|must-be-at-least-2000',
      'number.unsafe': 'validations|vehicle-year|too-large',
    }),

    series: Joi.string().allow(null).messages({
      'string.base': 'validations|vehicle-series|must-be-string',
    }),

    trim: Joi.string().allow(null).messages({
      'string.base': 'validations|vehicle-trim|must-be-string',
    }),

    manufacturedAt: Joi.date().allow(null).messages({
      'date.base': 'validations|vehicle-manufactured-at|must-be-a-valid-date',
    }),

    exteriorColorId: Joi.number().allow(null).messages({
      'number.base': 'validations|vehicle-exterior-color-id|must-be-number',
    }),

    interiorColorId: Joi.number().allow(null).messages({
      'number.base': 'validations|vehicle-interior-color-id|must-be-number',
    }),

    bodyType: Joi.alternatives()
      .conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid': 'validations|vehicle-body-type|cannot-be-null',
        }),
        otherwise: Joi.string()
          .valid(...Object.values(VehicleBodyTypeEnum))
          .required()
          .messages({
            'any.required': 'validations|vehicle-body-type|required',
            'any.only': 'validations|vehicle-body-type|invalid-value',
            'string.base': 'validations|vehicle-body-type|must-be-string',
            'string.empty': 'validations|vehicle-body-type|cannot-be-empty',
          }),
      })
      .required()
      .messages({
        'any.required': 'validations|vehicle-body-type|required',
      }),

    steeringPosition: Joi.string()
      .valid(...Object.values(VehicleSteeringPositionEnum))
      .allow(null)
      .messages({
        'string.base': 'validations|vehicle-steering-position|must-be-string',
        'any.only': 'validations|vehicle-steering-position|invalid-value',
      }),

    doors: Joi.number().allow(null).messages({
      'number.base': 'validations|vehicle-doors|must-be-number',
    }),

    seats: Joi.number().allow(null).messages({
      'number.base': 'validations|vehicle-doors|must-be-number',
    }),

    transmissionType: Joi.string()
      .valid(...Object.values(VehicleTransmissionTypeEnum))
      .allow(null)
      .messages({
        'string.base': 'validations|vehicle-transmission-type|must-be-string',
        'any.only': 'validations|vehicle-transmission-type|invalid-value',
      }),

    driveTrainType: Joi.string()
      .valid(...Object.values(VehicleDriveTrainTypeEnum))
      .allow(null)
      .messages({
        'string.base': 'validations|vehicle-drive-train-type|must-be-string',
        'any.only': 'validations|vehicle-drive-train-type|invalid-value',
      }),

    odometerUnit: Joi.string()
      .valid(...Object.values(VehicleOdometerUnitEnum))
      .allow(null)
      .messages({
        'string.base': 'validations|vehicle-odometer-unit|must-be-string',
        'any.only': 'validations|vehicle-odometer-unit|invalid-value',
      }),

    primaryPowerPlantType: Joi.string()
      .valid(...Object.values(VehiclePowerPlantTypeEnum))
      .allow(null)
      .messages({
        'string.base':
          'validations|vehicle-primary-power-plant-type|must-be-string',
        'any.only':
          'validations|vehicle-primary-power-plant-type|invalid-value',
      }),

    secondaryPowerPlantType: Joi.string()
      .valid(...Object.values(VehiclePowerPlantTypeEnum))
      .allow(null)
      .messages({
        'string.base':
          'validations|vehicle-secondary-power-plant-type|must-be-string',
        'any.only':
          'validations|vehicle-secondary-power-plant-type|invalid-value',
      }),

    engineSizeUnit: Joi.string()
      .valid(...Object.values(VehicleEngineSizeUnitEnum))
      .allow(null)
      .messages({
        'string.base': 'validations|vehicle-engine-size-unit|must-be-string',
        'any.only': 'validations|vehicle-engine-size-unit|invalid-value',
      }),

    engineSize: Joi.number().allow(null).messages({
      'number.base': 'validations|vehicle-engine-size|must-be-number',
    }),

    engineOutput: Joi.number().allow(null).messages({
      'number.base': 'validations|vehicle-engine-output|must-be-number',
    }),

    engineTurbine: Joi.boolean().allow(null).messages({
      'boolean.base': 'validations|vehicle-engine-turbine|must-be-boolean',
    }),

    averageEmissionsCO2: Joi.number().allow(null).messages({
      'number.base': 'validations|vehicle-average-emissions-co2|must-be-number',
    }),

    emissionsUnitCO2: Joi.string()
      .valid(...Object.values(VehicleEmissionsUnitCO2Enum))
      .allow(null)
      .messages({
        'string.base': 'validations|vehicle-emissions-unit-co2|must-be-string',
        'any.only': 'validations|vehicle-emissions-unit-co2|invalid-value',
      }),

    averageFuelConsumption: Joi.number().allow(null).messages({
      'number.base':
        'validations|vehicle-average-fuel-consumption|must-be-number',
    }),

    fuelConsumptionUnit: Joi.string()
      .valid(...Object.values(FuelConsumptionUnitEnum))
      .allow(null)
      .messages({
        'string.base':
          'validations|vehicle-fuel-consumption-unit|must-be-string',
        'any.only': 'validations|vehicle-fuel-consumption-unit|invalid-value',
      }),

    fuelTankCapacity: Joi.number().allow(null).messages({
      'number.base': 'validations|vehicle-fuel-tank-capacity|must-be-number',
    }),

    fuelTankCapacityUnit: Joi.string()
      .valid(...Object.values(VehicleFuelTankCapacityUnitEnum))
      .allow(null)
      .messages({
        'string.base':
          'validations|vehicle-fuel-tank-capacity-unit|must-be-string',
        'any.only': 'validations|vehicle-fuel-tank-capacity-unit|invalid-value',
      }),

    averageElectricConsumption: Joi.number().allow(null).messages({
      'number.base':
        'validations|vehicle-average-electric-consumption|must-be-number',
    }),

    electricBatteryCapacity: Joi.number().allow(null).messages({
      'number.base':
        'validations|vehicle-electric-battery-capacity|must-be-number',
    }),

    electricBatteryVoltage: Joi.number().allow(null).messages({
      'number.base':
        'validations|vehicle-electric-battery-voltage|must-be-number',
    }),

    electricBatteryType: Joi.string()
      .valid(...Object.values(VehicleElectricBatteryTypeEnum))
      .allow(null)
      .messages({
        'string.base':
          'validations|vehicle-electric-battery-type|must-be-string',
        'any.only': 'validations|vehicle-electric-battery-type|invalid-value',
      }),

    electricMotorOutput: Joi.number().allow(null).messages({
      'number.base': 'validations|vehicle-electric-motor-output|must-be-number',
    }),

    electricMotorRangeFrom: Joi.number().allow(null).messages({
      'number.base':
        'validations|vehicle-electric-motor-range-from|must-be-number',
    }),

    electricMotorRangeTo: Joi.number().allow(null).messages({
      'number.base':
        'validations|vehicle-electric-motor-range-to|must-be-number',
    }),

    locationId: Joi.alternatives()
      .conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid': 'validations|vehicle-location-id|cannot-be-null',
        }),
        otherwise: Joi.number().required().messages({
          'any.required': 'validations|vehicle-location-id|required',
          'number.base': 'validations|vehicle-location-id|must-be-number',
        }),
      })
      .required()
      .messages({
        'any.required': 'validations|vehicle-location-id|required',
      }),

    vehicleCategoryId: Joi.number().allow(null).messages({
      'number.base': 'validations|vehicle-category-id|must-be-number',
    }),

    supplierId: Joi.number().allow(null).messages({
      'number.base': 'validations|vehicle-supplier-id|must-be-number',
    }),

    featureItems: Joi.array().items(Joi.number()).allow(null),

    hasGpsTracker: Joi.boolean().allow(null),

    gpsTrackerUuid: Joi.string().allow(null, '').messages({
      'string.base': 'validations|vehicle-gps-tracker-uuid|must-be-string',
    }),

    purchaseListPrice: Joi.number().allow(null).messages({
      'number.base': 'validations|vehicle-purchase-list-price|must-be-number',
    }),

    purchasePaidPrice: Joi.number().allow(null).messages({
      'number.base': 'validations|vehicle-purchase-paid-price|must-be-number',
    }),

    purchasedAt: Joi.date().allow(null).messages({
      'date.base': 'validations|vehicle-purchased-at|must-be-a-valid-date',
    }),

    notes: Joi.string().allow(null, '').messages({
      'string.base': 'validations|vehicle-notes|must-be-string',
    }),

    // Tags
    tags: createTagsSchema().messages({
      'array.base': 'validations|vehicle-tags|invalid-value',
    }),

    policies: createPoliciesAssignSchema(AppSubjectEnum.VEHICLE),

    files: Joi.any().allow(null),
  });
